import React, { useState } from 'react';
import { gql, useQuery } from '@apollo/client';

// Components
import { CompanyPlayerListContainer } from './company-player-list.container'

// Data
import { getAuthUser } from '../../../lib/services/auth.service';
import { PLAYER_FIELDS } from '../../../lib/queries/player.queries';
import { buildAddPlayerToAssessment } from "../../../lib/services/player-assessment.service";
import { useCompanySport } from '../../../lib/stores/company.store';
import { ROLES } from '../../../../_configuration';
import { useRoleAllowedAgeGroups } from '../../../lib/services/age-group.service';

const GET_COMPANY_PLAYER_DROPDOWNS = gql`
  query GetCompanyPlayerDropdowns($assessmentId: ID!, $companyId: ID!, $includeGlobalForSport: SPORT) {
    ageGroups(assessmentId: $assessmentId) {
      _id
      name
    }
    positions(companyId: $companyId, includeGlobalForSport: $includeGlobalForSport) {
      _id
      name
    }
    assessments(companyId: $companyId) {
      _id
      name
    }
  }
`;

const GET_PLAYER_ASSESSMENTS = gql`
  query GetAssessmentTeams($assessmentId: ID!, $ageGroupIds: [ID!]!) {
    playerAssessments(assessmentId: $assessmentId, ageGroupIds: $ageGroupIds) {
      player {
        _id
      }
      team
      position {
        _id
      }
    }
  }
`;

const GET_CURRENT_PLAYER_LIST = gql`
  query GetCurrentPlayerList($assessmentId: ID!, $ageGroupIds: [ID!]!) {
    playerAssessments(assessmentId: $assessmentId, ageGroupIds: $ageGroupIds) {
      player {
        _id
      }
    }
  }
`;

const GET_COMPANY_PLAYER_LIST = gql`
  query GetCompanyPlayerList($companyId: ID!, $ignoreAssessmentId: ID) {
    playerAssessmentsForCompany(companyId: $companyId, ignoreAssessmentId: $ignoreAssessmentId) {
      player {
        ...PlayerFields
      }
    }
  }

  ${PLAYER_FIELDS}
`;

export const CompanyPlayerListGraphQL = () => {
  const user = getAuthUser();
  const sport = useCompanySport()

  const { addPlayerToAssessment } = buildAddPlayerToAssessment();
  const [activeAgeGroup, setActiveAgeGroup] = useState();
  const [activeCopyAssessment, setActiveCopyAssessment] = useState();
  const [activeCopyAgeGroup, setActiveCopyAgeGroup] = useState();

  const { data: teamData } = useQuery(GET_PLAYER_ASSESSMENTS, {
    skip: !activeCopyAssessment || !activeCopyAgeGroup,
    variables: {
      assessmentId: activeCopyAssessment,
      ageGroupIds: [activeCopyAgeGroup]
    },
    fetchPolicy: 'cache-and-network'
  });

  const { data: copyDropdownResult } = useQuery(GET_COMPANY_PLAYER_DROPDOWNS, {
    skip: !user || !activeCopyAssessment,
    variables: {
      assessmentId: activeCopyAssessment,
      companyId: user?.settings.activeCompanyId,
      includeGlobalForSport: sport
    },
    fetchPolicy: 'cache-and-network'
  });

  const { data: dropdownResult, loading: dropdownLoading, error: dropdownError } = useQuery(GET_COMPANY_PLAYER_DROPDOWNS, {
    skip: !user,
    variables: {
      assessmentId: user?.settings.activeAssessmentId,
      companyId: user?.settings.activeCompanyId,
      includeGlobalForSport: sport
    },
    fetchPolicy: 'cache-and-network'
  });

  const requiredRoles = [ROLES.CONFIGURATION];
  const ageGroups = useRoleAllowedAgeGroups({ageGroups: dropdownResult?.ageGroups, requiredRoles})
  const positions = (dropdownResult && dropdownResult.positions) || [];

  const copyAgeGroups = useRoleAllowedAgeGroups({ageGroups: copyDropdownResult?.ageGroups, requiredRoles})
  const copyPlayers = (teamData && teamData.playerAssessments) || [];
  const copyAssessments = (dropdownResult && dropdownResult.assessments) || [];

  const { data: currentPlayerListData, loading: currentPlayerListLoading, error: currentPlayerListError } = useQuery(GET_CURRENT_PLAYER_LIST, {
    skip: !user || !activeAgeGroup,
    variables: { assessmentId: user?.settings.activeAssessmentId, ageGroupIds: [activeAgeGroup] },
    fetchPolicy: 'cache-and-network'
  });

  const { data: companyPlayerListData, loading: companyPlayerListLoading, error: companyPlayerListError } = useQuery(GET_COMPANY_PLAYER_LIST, {
    skip: !user || !activeAgeGroup,
    variables: { companyId: user?.settings.activeCompanyId, ignoreAssessmentId: user?.settings.activeAssessmentId },
    fetchPolicy: 'cache-and-network'
  });

  const playerAssessments = (currentPlayerListData && currentPlayerListData.playerAssessments) || [];
  const players = (companyPlayerListData && companyPlayerListData.playerAssessmentsForCompany.map((pa: IPlayerAssessment) => pa.player)) || [];

  return <CompanyPlayerListContainer
    playerAssessments={playerAssessments}
    addPlayerToAssessment={addPlayerToAssessment}
    players={players}
    positions={positions}
    ageGroups={ageGroups}
    loading={currentPlayerListLoading || companyPlayerListLoading || dropdownLoading}
    fetchError={Boolean(currentPlayerListError) || Boolean(companyPlayerListError) || Boolean(dropdownError)}
    setActiveAgeGroup={setActiveAgeGroup}
    activeAgeGroup={activeAgeGroup}
    setActiveCopyAgeGroup={setActiveCopyAgeGroup}
    setActiveCopyAssessment={setActiveCopyAssessment}
    copyAgeGroups={copyAgeGroups}
    copyPlayers={copyPlayers}
    copyAssessments={copyAssessments}
  />;
};
